import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Brain, Sparkles, TrendingUp, AlertTriangle, Target, BarChart, PieChart, Zap, Eye, Shield, LineChart, Activity } from 'lucide-react';
import { DemoModal } from '@/components/DemoModal';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

// AI Capability Card
const AICapabilityCard = ({ icon: Icon, color, title, description, features }) => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 feature-icon ${color}`}>
      <Icon size={28} color="white" />
    </div>
    <h3 className="text-gray-900 font-bold text-lg mb-2">{title}</h3>
    <p className="text-gray-600 text-sm mb-4">{description}</p>
    <ul className="space-y-2">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
          <span className="text-[#2C39B8] mt-1">→</span>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Insight Card
const InsightCard = ({ icon: Icon, title, value, trend, color }) => (
  <div className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <Icon size={20} className={color} />
      <span className={`text-xs font-medium ${trend.includes('+') ? 'text-green-400' : 'text-red-400'}`}>{trend}</span>
    </div>
    <div className="text-white font-bold text-2xl mb-1">{value}</div>
    <div className="text-gray-400 text-sm">{title}</div>
  </div>
);

export default function AIBrainPage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <div data-testid="ai-brain-page">
      <Navbar onDemoClick={() => setDemoModalOpen(true)} />
      <SEOHead title="AI Brain - Conversational Analytics for Channel Loyalty" description="ChannelLoyalty AI Brain: Natural language analytics for B2B loyalty programs. Predictive churn detection, reward optimization, fraud prevention, and real-time channel intelligence." keywords="AI loyalty analytics, predictive churn detection, channel intelligence platform, B2B loyalty AI" canonical="https://channelloyalty.ai/ai-brain" />
      
      <main>
        {/* Banner Hero */}
        <section className="hero-banner-full">
          <div className="hero-banner-carousel">
            <div className="hero-banner-track">
              <div className="hero-banner-slide active">
                <img src="/images/banner_ai_brain.jpg" alt="ChannelLoyalty AI Brain - Stop Guessing, Start Predicting" />
              </div>
            </div>
          </div>
        </section>

        {/* Live Insights Demo */}
        <section className="section-light section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge">Live Intelligence</span>
              <h2>Real-Time AI Insights Dashboard</h2>
              <p>Your AI copilot surfaces critical insights before you even ask.</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <InsightCard icon={AlertTriangle} title="Churn Risk" value="23" trend="-5 from last week" color="text-red-400" />
              <InsightCard icon={TrendingUp} title="Growth Leaders" value="156" trend="+12% this month" color="text-green-400" />
              <InsightCard icon={Target} title="Scheme ROI" value="3.2x" trend="+0.4x vs target" color="text-blue-400" />
              <InsightCard icon={Activity} title="Active Partners" value="12.4K" trend="+892 this week" color="text-cyan-400" />
            </div>

            {/* Chat Interface Mock */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden max-w-3xl mx-auto">
              <div className="bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Brain size={24} className="text-white" />
                  <span className="text-white font-semibold">ChannelLoyalty AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm">You</div>
                  <div className="bg-gray-100 rounded-xl px-4 py-3 max-w-md">
                    <p className="text-gray-700 text-sm">Which dealers are at risk of churning this quarter?</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center">
                    <Brain size={16} className="text-white" />
                  </div>
                  <div className="bg-blue-50 rounded-xl px-4 py-3 max-w-md border border-blue-100">
                    <p className="text-gray-700 text-sm mb-2">I&apos;ve identified <strong>23 high-risk dealers</strong> based on:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 40%+ drop in order frequency</li>
                      <li>• Declining SKU diversity</li>
                      <li>• Competitor activity in their region</li>
                    </ul>
                    <p className="text-[#2C39B8] text-sm mt-2 font-medium">→ View full risk report</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI Capabilities */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge">AI Capabilities</span>
              <h2>Intelligence That Drives Action</h2>
              <p>From prediction to prescription — AI that thinks like your best analyst.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AICapabilityCard
                icon={Eye}
                color="#2C39B8"
                title="Predictive Analytics"
                description="Forecast churn, identify growth opportunities, and predict scheme outcomes."
                features={["Churn risk scoring", "Growth trajectory prediction", "Demand forecasting"]}
              />
              <AICapabilityCard
                icon={AlertTriangle}
                color="pink"
                title="Anomaly Detection"
                description="Automatically detect fraud, leakage, and suspicious claim patterns."
                features={["Duplicate claim detection", "Invoice anomalies", "Geographic fraud patterns"]}
              />
              <AICapabilityCard
                icon={LineChart}
                color="cyan"
                title="Attribution Intelligence"
                description="Track influence-to-sale attribution across your entire channel."
                features={["Influencer impact scoring", "Multi-touch attribution", "ROI by touchpoint"]}
              />
              <AICapabilityCard
                icon={Target}
                color="orange"
                title="Optimization Engine"
                description="AI-recommended actions to improve scheme performance and ROI."
                features={["Budget reallocation", "Reward optimization", "Territory balancing"]}
              />
              <AICapabilityCard
                icon={PieChart}
                color="green"
                title="Segmentation AI"
                description="Dynamic partner segmentation based on behavior, not just transactions."
                features={["Behavioral clustering", "Value-based tiers", "Engagement scoring"]}
              />
              <AICapabilityCard
                icon={Zap}
                color="blue"
                title="Natural Language Query"
                description="Ask questions in plain English. Get answers in seconds."
                features={["No SQL required", "Instant visualizations", "Exportable reports"]}
              />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="section-dark section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge" style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>How It Works</span>
              <h2 className="text-white">From Data to Decision in 3 Steps</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">1</div>
                <h4 className="text-white font-bold text-lg mb-2">Connect Your Data</h4>
                <p className="text-gray-400">Plug in sales, claims, and channel data from any source. Our AI ingests and normalizes automatically.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">2</div>
                <h4 className="text-white font-bold text-lg mb-2">AI Learns Your Business</h4>
                <p className="text-gray-400">Our models train on your specific channel dynamics, identifying patterns unique to your ecosystem.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">3</div>
                <h4 className="text-white font-bold text-lg mb-2">Get Actionable Insights</h4>
                <p className="text-gray-400">Receive real-time alerts, recommendations, and answers to any question about your channel.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="container relative z-10">
            <h2>Ready to Unlock AI-Powered Channel Intelligence?</h2>
            <p>See how ChannelLoyalty AI Brain can transform your loyalty operations.</p>
            <div className="cta-buttons">
              <button onClick={() => setDemoModalOpen(true)} className="btn btn-primary">
                Request AI Demo <ArrowRight size={18} />
              </button>
              <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                <MessageCircle size={18} /> Talk to Expert
              </a>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
      <DemoModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
}
